import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { CLIENT_PHASES, PAUSED_TONE, phaseProgress, type PhaseTone } from '../types'

export interface PhaseTrackProps {
  /** El texto de la fase, ya traducido por el server. Se muestra tal cual. */
  label: string
  /** La fase como dato. Es lo que decide cuántos segmentos se llenan. */
  tone: PhaseTone
  className?: string
}

/**
 * El avance del proyecto: en qué etapa del camino está, de cuántas.
 *
 * Se dibuja con lo que ya trae la tarjeta —la fase— y no con un porcentaje de
 * tareas: el listado del portal no manda contadores, y pedir el detalle de cada
 * proyecto para poder dibujar una barra serían N requests para un adorno. En el
 * detalle, donde los contadores sí existen, el avance se cuenta en tareas.
 *
 * Tres casos:
 * - Una fase del camino ⇒ segmentos llenos hasta ella + "Paso 2 de 4".
 * - `En pausa` ⇒ el camino en gris, sin paso: no es una etapa, es una pausa.
 * - Cualquier otra ⇒ no se dibuja nada. Mejor sin barra que con una inventada.
 *
 * El texto de la fase **no se traduce acá**: se muestra el `label` que mandó el
 * server. Lo único nuestro es el andamio ("Etapa 2 de 4"), que sí sale del
 * catálogo.
 *
 * El avance se calcula con `tone`, que no depende del idioma. Antes se
 * comparaba el `label` contra una lista en español, así que en `pt` y en `en`
 * la barra **no se dibujaba nunca**: la tarjeta perdía lo único que contesta
 * "¿cuánto falta?" para todo cliente que no hablara español.
 */
export function PhaseTrack({ label, tone, className }: PhaseTrackProps) {
  const t = useT()
  const progress = phaseProgress(tone)
  const paused = tone === PAUSED_TONE

  if (!progress && !paused) return null

  const step = progress?.step ?? 0
  const total = progress?.total ?? CLIENT_PHASES.length

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div
        className="flex items-center gap-1"
        role="img"
        aria-label={
          paused
            ? t('portal.track.pausedAria')
            : t('portal.track.stepWithPhase', { paso: step, total, fase: label })
        }
      >
        {CLIENT_PHASES.map((phase, index) => (
          <span
            key={phase}
            aria-hidden="true"
            className="h-1 flex-1 rounded-full"
            style={{
              backgroundColor:
                index < step
                  ? 'hsl(var(--phase-in-development))'
                  : paused
                    ? 'hsl(var(--phase-paused) / .35)'
                    : 'hsl(var(--hairline))',
            }}
          />
        ))}
      </div>

      <p className="num text-2xs text-text-3">
        {paused ? label : t('portal.track.step', { paso: step, total })}
      </p>
    </div>
  )
}
